import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { Plus, Trash2, Search, Loader2, Edit2, Check, X, AlertTriangle } from 'lucide-react'
import {
  getPostageDiscrepancies,
  lookupOrderForDiscrepancy,
  createPostageDiscrepancy,
  updatePostageDiscrepancy,
  deletePostageDiscrepancy,
} from '../lib/api'

const fmt = (v) =>
  v == null ? '—' : `$${Number(v).toFixed(2)}`

const fmtDate = (d) => {
  if (!d) return '—'
  try {
    return new Date(d + 'T00:00:00').toLocaleDateString('en-AU', {
      day: '2-digit', month: 'short', year: 'numeric',
    })
  } catch { return d }
}

// ---------------------------------------------------------------------------
// Add-discrepancy form (shown at top when addOpen=true)
// ---------------------------------------------------------------------------
function AddForm({ clientId, onSaved, onCancel }) {
  const qc = useQueryClient()
  const [orderNumber, setOrderNumber] = useState('')
  const [charge, setCharge]           = useState('')
  const [notes, setNotes]             = useState('')
  const [found, setFound]             = useState(null)   // matched order
  const [lookupErr, setLookupErr]     = useState('')
  const [searching, setSearching]     = useState(false)

  const createMutation = useMutation({
    mutationFn: (data) => createPostageDiscrepancy(clientId, data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['postage-discrepancies', clientId] })
      qc.invalidateQueries({ queryKey: ['admin', 'clients'] })
      onSaved()
    },
  })

  async function handleLookup() {
    if (!orderNumber.trim()) return
    setSearching(true)
    setLookupErr('')
    setFound(null)
    try {
      const order = await lookupOrderForDiscrepancy(clientId, orderNumber.trim())
      setFound(order)
    } catch {
      setLookupErr('Order not found for this client.')
    } finally {
      setSearching(false)
    }
  }

  function handleKeyDown(e) {
    if (e.key === 'Enter') { e.preventDefault(); handleLookup() }
  }

  function handleSave() {
    const c = parseFloat(charge)
    // Reject NaN + 0. Negative values are valid — they represent a
    // refund / credit on a previously billed shipping charge.
    if (isNaN(c) || c === 0) return
    createMutation.mutate({
      order_number:      found ? found.order_number : orderNumber.trim(),
      additional_charge: c,
      notes:             notes.trim() || null,
    })
  }

  return (
    <div className="card p-4 mb-4 border border-blue-200 bg-blue-50/40">
      <h4 className="text-sm font-semibold text-slate-700 mb-3">Add Postage Discrepancy</h4>

      {/* Order number lookup */}
      <div className="flex gap-2 mb-3">
        <div className="flex-1">
          <label className="block text-xs text-slate-500 mb-1">Order Number</label>
          <div className="flex gap-1.5">
            <input
              className="input flex-1 text-sm"
              placeholder="e.g. NV10095"
              value={orderNumber}
              onChange={e => { setOrderNumber(e.target.value); setFound(null); setLookupErr('') }}
              onKeyDown={handleKeyDown}
            />
            <button
              type="button"
              onClick={handleLookup}
              disabled={!orderNumber.trim() || searching}
              className="btn-secondary px-3"
            >
              {searching ? <Loader2 size={14} className="animate-spin" /> : <Search size={14} />}
            </button>
          </div>
          {lookupErr && <p className="text-xs text-red-500 mt-1">{lookupErr}</p>}
        </div>
      </div>

      {/* Matched order details */}
      {found && (
        <div className="bg-white rounded-lg border border-slate-200 p-3 mb-3 grid grid-cols-2 sm:grid-cols-3 gap-x-6 gap-y-1 text-xs">
          <div><span className="text-slate-400">Ship Date </span><span className="font-medium">{fmtDate(found.shipped_date)}</span></div>
          <div><span className="text-slate-400">Carrier </span><span className="font-medium">{found.carrier || '—'}</span></div>
          <div><span className="text-slate-400">Service </span><span className="font-medium">{found.service || '—'}</span></div>
          <div><span className="text-slate-400">Recipient </span><span className="font-medium">{found.recipient_name || '—'}{found.recipient_state ? ` (${found.recipient_state})` : ''}</span></div>
          <div><span className="text-slate-400">Tracking </span><span className="font-medium font-mono">{found.tracking_number || '—'}</span></div>
          <div><span className="text-slate-400">Original Shipping </span><span className="font-medium">{fmt(found.shipping_cost)}</span></div>
        </div>
      )}

      {/* Charge + notes */}
      <div className="flex gap-3 items-end">
        <div className="w-44">
          <label className="block text-xs text-slate-500 mb-1">
            Additional Charge ($) <span className="text-slate-400 font-normal">— negative for refund</span>
          </label>
          <input
            type="number"
            step="0.01"
            className="input w-full text-sm"
            placeholder="0.00"
            value={charge}
            onChange={e => setCharge(e.target.value)}
          />
        </div>
        <div className="flex-1">
          <label className="block text-xs text-slate-500 mb-1">Notes (optional)</label>
          <input
            className="input w-full text-sm"
            placeholder="e.g. Weight correction, zone upgrade…"
            value={notes}
            onChange={e => setNotes(e.target.value)}
          />
        </div>
        <div className="flex gap-2 pb-0.5">
          <button
            type="button"
            onClick={handleSave}
            disabled={createMutation.isPending || (!found && !orderNumber.trim()) || !charge}
            className="btn-primary text-sm px-4"
          >
            {createMutation.isPending ? <Loader2 size={14} className="animate-spin" /> : null}
            Save
          </button>
          <button type="button" onClick={onCancel} className="btn-secondary text-sm px-3">
            Cancel
          </button>
        </div>
      </div>
      {createMutation.isError && (
        <p className="text-xs text-red-500 mt-2">Failed to save discrepancy.</p>
      )}
    </div>
  )
}

// ---------------------------------------------------------------------------
// Inline edit row
// ---------------------------------------------------------------------------
function EditRow({ row, onSave, onCancel }) {
  const [charge, setCharge] = useState(String(row.additional_charge))
  const [notes, setNotes]   = useState(row.notes || '')

  return (
    <tr className="bg-blue-50">
      <td className="px-3 py-2 text-xs">{fmtDate(row.shipped_date)}</td>
      <td className="px-3 py-2 text-xs font-mono font-medium">{row.order_number}</td>
      <td className="px-3 py-2 text-xs">
        <span className="inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium bg-slate-100 text-slate-600">Manual</span>
      </td>
      <td className="px-3 py-2 text-xs">{row.carrier || '—'}</td>
      <td className="px-3 py-2 text-xs">{row.recipient_name ? `${row.recipient_name}${row.recipient_state ? ` (${row.recipient_state})` : ''}` : '—'}</td>
      <td className="px-3 py-2 text-xs text-right">{fmt(row.original_shipping_cost)}</td>
      <td className="px-3 py-2">
        <input
          type="number"
          step="0.01"
          value={charge}
          onChange={e => setCharge(e.target.value)}
          className="w-24 text-right text-xs rounded border border-blue-300 px-1.5 py-1 focus:outline-none focus:ring-1 focus:ring-blue-400"
          title="Negative for refund / credit"
        />
      </td>
      <td className="px-3 py-2">
        <input
          value={notes}
          onChange={e => setNotes(e.target.value)}
          className="w-full text-xs rounded border border-slate-200 px-1.5 py-1 focus:outline-none focus:ring-1 focus:ring-blue-400"
        />
      </td>
      <td className="px-3 py-2">
        <div className="flex gap-1">
          <button
            onClick={() => onSave({ additional_charge: parseFloat(charge), notes: notes.trim() || null })}
            className="p-1 rounded text-green-600 hover:bg-green-50"
          >
            <Check size={14} />
          </button>
          <button onClick={onCancel} className="p-1 rounded text-slate-400 hover:bg-slate-100">
            <X size={14} />
          </button>
        </div>
      </td>
    </tr>
  )
}

// ---------------------------------------------------------------------------
// Main tab
// ---------------------------------------------------------------------------
export default function PostageDiscrepanciesTab({ clientId }) {
  const qc = useQueryClient()
  const [addOpen, setAddOpen] = useState(false)
  const [editId, setEditId]   = useState(null)

  // Period selector — defaults to current month so last month's discrepancies
  // (which belong on last month's invoice) don't keep appearing.
  const now = new Date()
  const [year,  setYear]  = useState(now.getFullYear())
  const [month, setMonth] = useState(now.getMonth() + 1)
  const [allPeriods, setAllPeriods] = useState(false)

  const { data: discrepancies = [], isLoading, error } = useQuery({
    queryKey: ['postage-discrepancies', clientId, allPeriods ? 'all' : `${year}-${month}`],
    queryFn: () => getPostageDiscrepancies(clientId, allPeriods ? { allPeriods: true } : { year, month }),
    enabled: !!clientId,
  })

  const updateMutation = useMutation({
    mutationFn: ({ id, data }) => updatePostageDiscrepancy(id, data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['postage-discrepancies', clientId] })
      qc.invalidateQueries({ queryKey: ['admin', 'clients'] })
      setEditId(null)
    },
  })

  const deleteMutation = useMutation({
    mutationFn: (id) => deletePostageDiscrepancy(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['postage-discrepancies', clientId] })
      qc.invalidateQueries({ queryKey: ['admin', 'clients'] })
    },
  })

  function handleDelete(row) {
    if (!window.confirm(`Delete discrepancy for order ${row.order_number}?`)) return
    deleteMutation.mutate(row.id)
  }

  const grandTotal = discrepancies.reduce((s, r) => s + (r.additional_charge || 0), 0)

  if (isLoading) {
    return <div className="card p-6"><div className="skeleton h-32 rounded" /></div>
  }

  return (
    <div className="space-y-4">
      {addOpen && (
        <AddForm
          clientId={clientId}
          onSaved={() => setAddOpen(false)}
          onCancel={() => setAddOpen(false)}
        />
      )}

      <div className="card overflow-hidden">
        <div className="flex flex-wrap items-center justify-between gap-3 px-4 py-3 border-b border-slate-100">
          <h3 className="text-sm font-semibold text-slate-700">Postage Discrepancies</h3>
          <div className="flex items-center gap-2">
            {/* Period selector — discrepancies are billing-period-scoped, so
                the tab defaults to the current month and last month's
                discrepancies don't roll over into this month's view. */}
            <select
              value={allPeriods ? 'all' : `${year}-${month}`}
              onChange={(e) => {
                const v = e.target.value
                if (v === 'all') { setAllPeriods(true) }
                else {
                  const [y, m] = v.split('-').map(Number)
                  setAllPeriods(false); setYear(y); setMonth(m)
                }
              }}
              className="input text-xs py-1 w-44"
              title="Discrepancies belong to the month they were entered. Last month's roll into last month's invoice."
            >
              {(() => {
                const opts = []
                for (let i = 0; i < 12; i++) {
                  const d = new Date(now.getFullYear(), now.getMonth() - i, 1)
                  const y = d.getFullYear(), m = d.getMonth() + 1
                  const label = d.toLocaleDateString('en-AU', { month: 'long', year: 'numeric' })
                  opts.push(<option key={`${y}-${m}`} value={`${y}-${m}`}>{label}</option>)
                }
                opts.push(<option key="all" value="all">All periods</option>)
                return opts
              })()}
            </select>
            <button
              onClick={() => { setAddOpen(true); setEditId(null) }}
              disabled={addOpen}
              className="btn-primary text-xs py-1.5 px-3"
            >
              <Plus size={13} /> Add Discrepancy
            </button>
          </div>
        </div>

        {/* Error before empty: the `= []` default on a failed fetch would
            otherwise render "no discrepancies recorded" — a billable charge
            looking like nothing to bill. */}
        {error ? (
          <div className="p-8 text-center">
            <AlertTriangle size={24} className="mx-auto text-red-500 mb-2" />
            <p className="text-sm font-medium text-red-700">Couldn't load postage discrepancies</p>
            <p className="text-xs text-red-600 mt-1">
              This is a load failure, not an empty list — there may be discrepancies
              for this period that aren't shown here or counted in the total.
            </p>
            <p className="text-xs text-slate-400 mt-1.5 font-mono">
              {error?.response?.data?.detail || error?.message || 'Unknown error'}
            </p>
          </div>
        ) : discrepancies.length === 0 ? (
          <div className="p-8 text-center text-slate-400 text-sm">
            No postage discrepancies recorded.
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-slate-50 border-b border-slate-100 text-xs text-slate-500 uppercase tracking-wide">
                  <th className="px-3 py-2.5 text-left font-medium">Ship Date</th>
                  <th className="px-3 py-2.5 text-left font-medium">Order #</th>
                  <th className="px-3 py-2.5 text-left font-medium">Source</th>
                  <th className="px-3 py-2.5 text-left font-medium">Carrier</th>
                  <th className="px-3 py-2.5 text-left font-medium">Recipient</th>
                  <th className="px-3 py-2.5 text-right font-medium">Orig. Shipping</th>
                  <th className="px-3 py-2.5 text-right font-medium">Discrepancy</th>
                  <th className="px-3 py-2.5 text-left font-medium">Notes</th>
                  <th className="px-3 py-2.5 text-left font-medium w-20"></th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {discrepancies.map((row, idx) =>
                  editId === row.id ? (
                    <EditRow
                      key={row.id}
                      row={row}
                      onSave={(data) => updateMutation.mutate({ id: row.id, data })}
                      onCancel={() => setEditId(null)}
                    />
                  ) : (
                    <tr key={row.id} className={idx % 2 === 1 ? 'bg-slate-50/50' : ''}>
                      <td className="px-3 py-2.5 text-xs text-slate-600">{fmtDate(row.shipped_date)}</td>
                      <td className="px-3 py-2.5 text-xs font-mono font-medium text-slate-800">{row.order_number}</td>
                      <td className="px-3 py-2.5 text-xs">
                        {row.source === 'fedex_reconciliation' ? (
                          <span
                            className="inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium bg-orange-100 text-orange-700"
                            title={`Auto-generated from FedEx invoice ${row.source_invoice_number || ''} — order shipped in a locked billing month.`}
                          >
                            FedEx
                          </span>
                        ) : (
                          <span className="inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium bg-slate-100 text-slate-600">
                            Manual
                          </span>
                        )}
                      </td>
                      <td className="px-3 py-2.5 text-xs text-slate-600">{row.carrier || '—'}</td>
                      <td className="px-3 py-2.5 text-xs text-slate-600">
                        {row.recipient_name
                          ? `${row.recipient_name}${row.recipient_state ? ` (${row.recipient_state})` : ''}`
                          : '—'}
                      </td>
                      <td className="px-3 py-2.5 text-xs text-right text-slate-600">{fmt(row.original_shipping_cost)}</td>
                      <td className={`px-3 py-2.5 text-xs text-right font-semibold ${Number(row.additional_charge) < 0 ? 'text-emerald-700' : 'text-amber-700'}`}>
                        {fmt(row.additional_charge)}
                      </td>
                      <td className="px-3 py-2.5 text-xs text-slate-500">{row.notes || '—'}</td>
                      <td className="px-3 py-2.5">
                        <div className="flex gap-1">
                          {/* Auto-generated FedEx rows are read-only — the source of
                              truth is the FedEx reconciliation. Hide edit; allow delete
                              only with a warning, since the next ingest would re-create it. */}
                          {row.source !== 'fedex_reconciliation' && (
                            <button
                              onClick={() => setEditId(row.id)}
                              className="p-1 rounded text-slate-300 hover:text-blue-600 hover:bg-blue-50 transition-colors"
                            >
                              <Edit2 size={13} />
                            </button>
                          )}
                          <button
                            onClick={() => handleDelete(row)}
                            disabled={deleteMutation.isPending}
                            className="p-1 rounded text-slate-300 hover:text-red-500 hover:bg-red-50 transition-colors"
                            title={
                              row.source === 'fedex_reconciliation'
                                ? 'Auto-generated. Will be re-created by the next FedEx ingest unless the recon row is also resolved.'
                                : undefined
                            }
                          >
                            <Trash2 size={13} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  )
                )}
              </tbody>
              <tfoot>
                <tr className="bg-blue-50 border-t-2 border-blue-200">
                  <td colSpan={6} className="px-3 py-2.5 text-xs font-semibold text-slate-700">
                    Total ({discrepancies.length} {discrepancies.length === 1 ? 'discrepancy' : 'discrepancies'})
                  </td>
                  <td className={`px-3 py-2.5 text-xs font-bold text-right ${grandTotal < 0 ? 'text-emerald-700' : 'text-amber-700'}`}>{fmt(grandTotal)}</td>
                  <td colSpan={2} />
                </tr>
              </tfoot>
            </table>
          </div>
        )}
      </div>
    </div>
  )
}
